import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  HelpTypes,
  adminActions,
  adminResetValue,
  getAdminReportsFilters,
  resetStatus,
  successOrSelector,
  MiscType,
  adminReports,
  ServiceTypes,
  Agencies,
  successSelector,
  ICalendarEventType,
  adminChargesPDF,
  adminInvoicePDF,
  handleAll,
} from '@pharmaplan/common';
import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppDispatch } from '../useAppDispatch';
import { IDynamicTableObject } from '../../components/DynamicTable/types';

import useAdminReportsHandler from './useAdminReportsHandler';
import { IOwnershipBookingsDates } from './useAdminReportsFn';
import useAutoComplete from '../useAutocomplete';
import {
  MomentTimeFormats,
  OwnershipTypes,
  customDropdownTypes,
} from '../../helpers/Constants';
import { useAppSelector } from '../useAppSelector';
import useDrawer from '../useDrawer';
import { showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import strings from '../../localization';
import useTableSelectCheckbox from '../useTableSelectCheckbox';
import { hasAction } from '../../components/Reports/helpers';
import useWorkshiftDelete from '../Admin/useWorkshiftDelete';
import usePharmacyOwnership from '../Admin/PharmacyList/usePharmacyOwnership';
import useTableSort from '../useTableSort';
import useAdmin from '../useAdmin';
import { downloadPDF } from '../../helpers/Functions';

const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

const { All } = customDropdownTypes;
const { pharmacist } = HelpTypes;

/**
 * A super admin sees every account by default; a regular admin is scoped to
 * their own, so the ownership filter starts where each one's reach ends.
 */
const buildInitialValues = (isSuperAdmin: boolean) =>
  ({
    type: '',
    startDate: null,
    endDate: null,
    replacementType: All,
    pharmacistId: '',
    pharmacists: '',
    pharmacistSelected: '',
    availabilityStatus: All,
    softwareId: '',
    city: '',
    userType: MiscType.All,
    source: isSelfService ? Agencies.PharmaGap : Agencies.PharmaPlan,
    ownership: isSuperAdmin ? OwnershipTypes.all : OwnershipTypes.myAccounts,
    adminName: '',
    ownerId: '',
  });

const {
  approveBooking,
  requestCancellation,
  deletePharmacistAvailabilities,
  expressBooking,
  deletePharmacyWorkshift,
  editAdminWorkshift,
  deleteMultipleWorkshift,
  selfApproveBooking,
  adminUpdateShiftContract,
  adminCancelMultiple,
  adminDeleteContract,
} = adminActions;

const successActions = [
  approveBooking,
  requestCancellation,
  deletePharmacistAvailabilities,
  expressBooking,
  deletePharmacyWorkshift,
  editAdminWorkshift,
  deleteMultipleWorkshift,
  selfApproveBooking,
  adminUpdateShiftContract,
  adminDeleteContract,
];
const deleteActions = [
  deletePharmacistAvailabilities,
  deletePharmacyWorkshift,
  deleteMultipleWorkshift,
  adminDeleteContract,
];
const cancelActions = [
  adminActions.adminCancelBooking,
  adminCancelMultiple,
];

/**
 * The ownership report matches on a typed admin name; every other report picks
 * an owner from the roster. Either way a chosen owner widens the scope to that
 * admin's accounts, so it supersedes "My accounts" rather than stacking with it.
 */
const buildOwnershipParams = (values: FormikValues, isOwnership: boolean) => {
  const { ownership, adminName, ownerId } = values ?? {};

  if (isOwnership) {
    return { adminName };
  }

  return {
    ownerId,
    onlyCurrentUser: !ownerId && ownership === OwnershipTypes.myAccounts,
  };
};

const formatFilterDate = (date: unknown) =>
  (date ? (date as DateTime).toFormat(MomentTimeFormats.fullDate) : null);

const useAdminReports = () => {
  const [page, setPage] = useState(1);
  const [selectedCount, setSelectedCount] = useState(0);
  /**
   * The ownership bookings drill-down has to be fetched for the range the
   * report itself was fetched with, so the dates are kept as they were sent
   * rather than read back off the form, which may have moved on since.
   */
  const [appliedDates, setAppliedDates] = useState<IOwnershipBookingsDates>({
    startDate: null,
    endDate: null,
  });

  const dispatch = useAppDispatch();

  const { pathname } = useLocation();
  const { isSuperAdmin } = useAdmin();
  const { isDesc, orderBy, handleSorting, order, resetSorting } = useTableSort();

  const initialValues = useMemo(
    () =>
      buildInitialValues(isSuperAdmin),
    [isSuperAdmin],
  );

  const data = useAppSelector(adminReports);
  const chargesPDF = useAppSelector(adminChargesPDF);
  const invoicePDF = useAppSelector(adminInvoicePDF);

  const chargesSuccess = useAppSelector((state) =>
    successSelector([adminActions.adminDownloadChargesReport], state));
  const invoiceSuccess = useAppSelector((state) =>
    successSelector([adminActions.adminDownloadProfessionalInvoice], state));

  const cancelSuccess = useAppSelector((state) =>
    successOrSelector(cancelActions, state));

  const selectableData = useMemo(
    () =>
      data?.data
        .filter((item) =>
          hasAction(item.startDate))
        .map((item) =>
          item.workshiftId),
    [data.data],
  );

  const {
    selectAll,
    selectedIds,
    handlePress,
    handleSelectedIds,
    handleSelectAll,
    allIds,
    handleReset: resetCheckboxes,
  } = useTableSelectCheckbox({
    page,
    useOfEdit: false,
    userType: MiscType.All,
    dataLength: selectableData.length,
  });

  const { showMultipleWorkshiftsDelete } = useWorkshiftDelete();

  const customSelectAll = (toBeSet: boolean) => {
    let ids: Array<string> = [];

    if (toBeSet) {
      ids = selectableData;
      handleSelectedIds({ [page]: ids });
      const totalPostedCount = data?.data
        .filter((item) =>
          hasAction(item.startDate))
        .reduce(
          (prev, curr) =>
            prev
            + ((curr?.totalShiftCount || 0) - (curr?.bookedShiftCount ?? 0) || 1),
          0,
        );

      setSelectedCount(totalPostedCount);
    } else {
      setSelectedCount(0);
      handleSelectedIds({ [page]: [] });
    }

    handleSelectAll({ [page]: toBeSet });
  };

  const customHandlePress = (id: string, count?: number) =>
    () => {
      if (selectedIds.includes(id)) {
        setSelectedCount((prev) =>
          prev - (count || 1));
      } else {
        setSelectedCount((prev) =>
          prev + (count || 1));
      }

      handlePress(id)();
    };

  const deleteWorkshifts = () => {
    showMultipleWorkshiftsDelete(allIds, selectedCount);
  };

  const rememberAppliedDates = (start: unknown, end: unknown) => {
    const startDate = formatFilterDate(start);
    const endDate = formatFilterDate(end);

    setAppliedDates((prev) =>
      (prev.startDate === startDate && prev.endDate === endDate
        ? prev
        : { startDate, endDate }));
  };

  const {
    api,
    reportType,
    headers,
    rows,
    handleDropdown,
    isAvailabilities,
    isPosted,
    isOwnership,
  } = useAdminReportsHandler(selectedIds, customHandlePress, appliedDates);

  const { closeDrawer } = useDrawer();

  const { ownershipSuccess, acknowledgeOwnershipChange } = usePharmacyOwnership();

  const { debouncedInput } = useAutoComplete({
    userTypes: [pharmacist],
  });

  const successes = useAppSelector((state) =>
    successOrSelector(successActions, state));

  const deleteSuccess = useAppSelector((state) =>
    successOrSelector(deleteActions, state));

  const onSubmit = (values: FormikValues) => {
    const {
      startDate,
      endDate,
      replacementType,
      pharmacistId,
      availabilityStatus,
      pharmacistSelected,
      softwareId,
      city,
      source,
      userType,
    } = values ?? {};

    setPage(1);
    resetCheckboxes();
    rememberAppliedDates(startDate, endDate);

    dispatch(
      api({
        startDate: formatFilterDate(startDate),
        endDate: formatFilterDate(endDate),
        type: reportType,
        page: 1,
        replacementType: handleDropdown(replacementType) as number,
        availabilityStatus: handleDropdown(availabilityStatus) as number,
        userType: isAvailabilities ? handleAll<HelpTypes>(userType) as HelpTypes : '',
        pharmacistId: pharmacistSelected ? pharmacistId : '',
        softwareId,
        city: isPosted ? city : '',
        sortBy: orderBy,
        desc: isDesc,
        source,
        ...buildOwnershipParams(values, isOwnership),
      }),
    );
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true,
  });

  const { totalCount } = data ?? {};
  const { resetForm, values } = formik ?? {};
  const {
    replacementType,
    availabilityStatus,
    userType,
    pharmacists,
    pharmacistId,
    softwareId,
    city,
    startDate,
    endDate,
  } = values ?? {};

  const handledType = handleDropdown(replacementType) as number;
  const handledStatus = handleDropdown(availabilityStatus) as number;
  const handledUserType = handleAll<HelpTypes>(userType);

  const callWithFilters = (
    pagination?: number,
    sortBy?: string,
    desc?: boolean,
  ) => {
    rememberAppliedDates(startDate, endDate);

    dispatch(
      api({
        startDate: formatFilterDate(startDate),
        endDate: formatFilterDate(endDate),
        page: pagination ?? 1,
        type: reportType,
        replacementType: handledType,
        availabilityStatus: handledStatus,
        userType: isAvailabilities ? handledUserType as HelpTypes : '',
        pharmacistId: values.pharmacistSelected ? pharmacistId : '',
        softwareId,
        city: isPosted ? city : '',
        sortBy,
        desc,
        ...buildOwnershipParams(values, isOwnership),
      }),
    );
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const gotoPage = selectedPage + 1;
    setPage(gotoPage);
    callWithFilters(gotoPage, orderBy, isDesc);
  };

  const table: IDynamicTableObject = {
    headerBar: [],
    headers,
    rows,
  };

  const handleReset = () => {
    resetForm();
    setPage(1);
    dispatch(adminResetValue(['adminReports']));
  };

  useEffect(() => {
    if (successes && startDate && endDate) {
      callWithFilters(page);
      dispatch(resetStatus(successActions));
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      closeDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successes]);

  // Reassigning or removing an owner from the drawer changes the counts and
  // hours behind it, so the report is refetched in place.
  useEffect(() => {
    if (ownershipSuccess) {
      acknowledgeOwnershipChange();
      callWithFilters(page, orderBy, isDesc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownershipSuccess]);

  useEffect(() => {
    if (cancelSuccess) {
      callWithFilters(page);
      closeDrawer();
      dispatch(resetStatus(cancelActions));
      showSuccess(dispatch, strings.bookingCancelledSuccessfully);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelSuccess]);

  useEffect(() => {
    debouncedInput(pharmacists);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacists]);

  useEffect(() => {
    resetSorting();
    if (!isOwnership) {
      dispatch(getAdminReportsFilters(reportType as ICalendarEventType));
    }
    if (startDate && endDate) {
      callWithFilters();
      setPage(1);
    } else {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (deleteSuccess) {
      showSuccess(dispatch, strings.recordDeletedSuccessfully);
      resetCheckboxes();
      dispatch(resetStatus(deleteActions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      callWithFilters(1, orderBy, isDesc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  useEffect(() => {
    if (chargesSuccess) {
      downloadPDF(
        chargesPDF?.pdfContent,
        `${strings.chargesReport} ${chargesPDF.chargedDate?.split?.('T')?.[0]}`,
      );
      dispatch(resetStatus([adminActions.adminDownloadChargesReport]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargesSuccess]);

  useEffect(() => {
    if (invoiceSuccess) {
      downloadPDF(invoicePDF?.pdfContent, invoicePDF.invoiceNumber);
      dispatch(resetStatus([adminActions.adminDownloadProfessionalInvoice]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceSuccess]);

  return {
    table,
    page,
    totalCount,
    handlePagination,
    handleReset,
    formik,
    selectAll,
    deleteWorkshifts,
    selectAllFunc: customSelectAll,
    selectableData,
    orderBy,
    isDesc,
    handleSorting,
    order,
  };
};

export default useAdminReports;
